import { colors } from "~/application/theme";
import { Card } from "~/components/Card";
import { EmptyState } from "~/components/EmptyState";
import { Grid } from "~/components/Grid";
import { Skeleton } from "~/components/Skeleton";
import { DonutChart } from "~/presentation/core/components/DonutChart/DonutChart";
import { ComplianceItem, SummaryResponse } from "../types";
import { asPercent } from "../utils";

export interface PolicyDonutsProps {
  summary?: SummaryResponse;
  isFetching: boolean;
}

const GRID_CSS = {
  gap: "$4",
  gridTemplateColumns: "repeat(1, 1fr)",
  "@sm": { gridTemplateColumns: "repeat(2, 1fr)" },
  "@lg": { gridTemplateColumns: "repeat(3, 1fr)" },
} as const;

const DONUT_COLORS = [colors["primary-700"], colors["error-base"]];

const INSIDE_LABEL = "Dentro da Política";
const OUTSIDE_LABEL = "Fora da Política";

const DONUT_COUNT = 3;

const buildDonutData = (item: ComplianceItem) => {
  const total = item?.total ?? 0;
  const violated = item?.violated ?? 0;
  const adherencePct = total > 0 ? Math.round(((total - violated) / total) * 100) : 0;
  const violationPct = 100 - adherencePct;
  return [
    { name: INSIDE_LABEL, value: adherencePct },
    { name: OUTSIDE_LABEL, value: violationPct },
  ];
};

export const PolicyDonuts = ({ summary, isFetching }: PolicyDonutsProps) => {
  if (isFetching || !summary) {
    return (
      <Grid css={GRID_CSS}>
        {Array.from({ length: DONUT_COUNT }, (_, index) => (
          <Card key={`policy-donut-skeleton-${index}`} css={{ p: "$6", height: "100%" }}>
            <Skeleton variant="text-6" css={{ width: "60%" }} />
            <Skeleton css={{ width: "100%", height: "260px", mt: "$4" }} />
          </Card>
        ))}
      </Grid>
    );
  }

  const { compliance } = summary;

  const hasComplianceData =
    compliance.lowerFare.total > 0 ||
    compliance.fixedValue.total > 0 ||
    compliance.leadTime.total > 0;

  if (!hasComplianceData) {
    return <EmptyState>Nenhum dado de conformidade disponível para o período.</EmptyState>;
  }

  const donuts = [
    {
      key: "lower-fare",
      title: `Menor Tarifa (${asPercent(compliance.lowerFare.rate)})`,
      data: buildDonutData(compliance.lowerFare),
    },
    {
      key: "fixed-value",
      title: `Valor Fixo (${asPercent(compliance.fixedValue.rate)})`,
      data: buildDonutData(compliance.fixedValue),
    },
    {
      key: "lead-time",
      title: `Antecedência (${asPercent(compliance.leadTime.rate)})`,
      data: buildDonutData(compliance.leadTime),
    },
  ];

  return (
    <Grid css={GRID_CSS}>
      {donuts.map((donut) => (
        <Card key={donut.key} css={{ p: "$6", height: "100%" }}>
          <DonutChart
            title={donut.title}
            data={donut.data}
            colors={DONUT_COLORS}
            legendPosition="bottom"
            height={260}
            innerRadius="62%"
            outerRadius="92%"
          />
        </Card>
      ))}
    </Grid>
  );
};

PolicyDonuts.displayName = "PolicyDonuts";
